import React, { useEffect, useState } from 'react';
import { Archive, ChevronDown, ChevronUp, Clock, Download, FileText, Forward, ListPlus, Mail, Reply, ReplyAll, ShieldAlert, ShieldCheck, Star, Trash2 } from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Avatar, LabelPill } from './ui.jsx';

// Render a markdown/plaintext body to SANITIZED HTML. breaks:true keeps plain-text
// line breaks; DOMPurify strips any dangerous markup from (untrusted) mail.
function renderMarkdown(src) {
  try { return DOMPurify.sanitize(marked.parse(src || '', { gfm: true, breaks: true })); }
  catch { return DOMPurify.sanitize((src || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))); }
}

/** Reading pane with action bar, security receipt, body (decrypted when a PQC envelope) and attachments. */
export function MessageView({ email, session, onArchive, onDelete, onJunk, onSnooze, onUnread, onStar, onReply, onForward, onTask }) {
  const { jmap, crypto } = session;
  const [full, setFull] = useState(null);
  const [text, setText] = useState('');
  const [sig, setSig] = useState(null); // { encrypted, valid, pending, suite, signer, transport }
  const [details, setDetails] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setFull(null); setText(''); setSig(null); setErr(null); setDetails(false);
    (async () => {
      try {
        const msg = await jmap.getEmailBody(email.id); setFull(msg);
        const raw = extractText(msg); const envs = tryParsePqc(raw);
        if (!envs) { setText(raw || email.preview || ''); setSig({ encrypted: false }); return; }
        // A PQC message carries one envelope per recipient (and one for the sender,
        // so the Sent copy is readable). Pick the envelope addressed to me.
        const me = (session.email || '').toLowerCase();
        const env = envs.find((e) => (e.to || '').toLowerCase() === me) || null;
        if (!env) { setSig({ encrypted: true, notForMe: true, suite: envs[0].suite, signer: envs[0].from }); setText(''); return; }
        const senderPub = await crypto.lookupRecipient(env.from);
        if (session.identity?.kem) {
          const out = await crypto.open({ envelope: env, recipientKemPrivateKey: session.identity.kem.privateKey, senderSigPublicKey: senderPub?.sigPublicKey });
          setText(out.plaintext); setSig({ encrypted: true, valid: out.signature?.valid !== false, suite: env.suite, signer: env.from });
        } else { setSig({ encrypted: true, pending: true, suite: env.suite, signer: env.from }); setText(''); }
      } catch (e) { setErr(e.message); }
    })();
  }, [email.id, jmap, crypto]); // eslint-disable-line

  const from = full?.from?.[0] || { name: email.fromName, email: email.fromEmail };
  const receipt = sig?.encrypted
    ? [['Suite', sig.suite || 'KM1-x25519.mlkem768-mldsa65-aes256gcm'], ['Key exchange', 'X25519 + ML-KEM-768'], ['Signature', `Ed25519 + ML-DSA-65 · ${sig.pending ? 'not yet verified' : sig.valid ? 'both valid' : 'INVALID'}`], ['Cipher', 'AES-256-GCM · HKDF-SHA256'], ['Signer', sig.signer], ['Transport', 'TLS 1.3 · X25519MLKEM768']]
    : [['Sender', `${from.email}${session.directoryHas?.(from.email) ? '' : ' · no PQC key in directory'}`], ['Transport', 'TLS 1.3 · x25519 (classical fallback)'], ['At rest', 'Encrypted on server']];

  const Act = ({ icon: I, label, onClick, color }) => <button className="km-btn-ghost" title={label} onClick={onClick} style={color ? { color } : undefined}><I size={16} />{label}</button>;
  return (
    <>
      <div className="km-actions">
        <Act icon={Archive} label="Archive" onClick={onArchive} /><Act icon={Trash2} label="Delete" onClick={onDelete} /><Act icon={Clock} label="Snooze" onClick={onSnooze} /><Act icon={Mail} label="Mark unread" onClick={onUnread} />
        <Act icon={Star} label={email.starred ? 'Starred' : 'Star'} onClick={onStar} color={email.starred ? 'var(--km-star)' : undefined} /><Act icon={ShieldAlert} label="Junk" onClick={onJunk} />
        <span className="km-faint" style={{ marginLeft: 'auto', fontSize: 12 }}>{fmtDate(email.receivedAt)}</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <article className="km-message">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}><h1>{email.subject || '(no subject)'}</h1>{(email.labels || []).map((l) => <LabelPill key={l} name={l} />)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><Avatar name={from.name || from.email} size={38} /><div style={{ minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600 }}>{from.name || from.email} <span className="km-muted" style={{ fontWeight: 400 }}>{from.name ? from.email : ''}</span></div><div className="km-faint" style={{ fontSize: 13 }}>to {(full?.to || []).map((t) => t.name || t.email).join(', ') || 'me'}</div></div></div>
          {sig && (
            <div className="km-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px' }}>
                {sig.encrypted && sig.valid !== false ? <ShieldCheck size={16} color="var(--km-ok)" /> : <ShieldAlert size={16} color={sig.encrypted ? 'var(--km-bad)' : 'var(--km-warn)'} />}
                <span style={{ flex: 1, fontSize: 13 }}>{sig.encrypted ? (sig.notForMe ? 'End-to-end encrypted - but no copy on this message is addressed to you, so it cannot be opened here.' : sig.pending ? 'End-to-end encrypted with post-quantum keys. Unlock your key to read.' : sig.valid ? 'End-to-end encrypted with post-quantum keys. Signature verified.' : 'Encrypted, but the signature does NOT verify.') : 'Not end-to-end encrypted. Delivered from an external server over TLS.'}</span>
                <button className="km-btn-ghost" style={{ padding: 0, fontSize: 12.5 }} onClick={() => setDetails((d) => !d)}>{details ? 'Hide details' : 'Details'}{details ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
              </div>
              {details && <div className="km-receipt">{receipt.map(([k, v]) => <React.Fragment key={k}><span className="km-faint">{k}</span><span className="km-mono">{v}</span></React.Fragment>)}</div>}
            </div>
          )}
          {err && <div style={{ color: 'var(--km-bad)', fontSize: 13 }}>{err}</div>}
          <MessageBody text={text} loading={!full} />
          {full?.attachments?.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>{full.attachments.map((a) => <a key={a.blobId} className="km-attach" href={jmap.downloadUrl?.(a) || '#'} download={a.name}><FileText size={20} color="var(--km-muted)" /><span><b>{a.name}</b><small>{fmtSize(a.size)}</small></span><Download size={15} color="var(--km-faint)" /></a>)}</div>}
          <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
            <button className="km-btn-outline" onClick={() => onReply(false)}><Reply size={15} />Reply</button>
            <button className="km-btn-outline" onClick={() => onReply(true)}><ReplyAll size={15} />Reply all</button>
            <button className="km-btn-outline" onClick={() => onForward(text)}><Forward size={15} />Forward</button>
            <button className="km-btn-ghost" style={{ marginLeft: 'auto' }} onClick={onTask}><ListPlus size={15} />Add to tasks</button>
          </div>
        </article>
      </div>
    </>
  );
}

// Body renderer: shows the author's own text, and collapses the quoted reply
// history (the "On … wrote:" + `>`-prefixed trailer) behind a toggle, styled as
// a clean blockquote instead of dumping raw `>` markers.
function MessageBody({ text, loading }) {
  const [showQuoted, setShowQuoted] = useState(false);
  if (loading) return <div className="km-body-text"><span className="km-faint">Loading…</span></div>;
  const { main, quoted } = splitQuoted(text || '');
  return (
    <div className="km-body-text km-md">
      {main ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(main) }} /> : (!quoted && <p className="km-faint">(no content)</p>)}
      {quoted && (
        <div style={{ marginTop: main ? 8 : 0 }}>
          <button className="km-btn-ghost" style={{ padding: '2px 10px', fontSize: 12.5, borderRadius: 999, border: '1px solid var(--km-line2)' }} onClick={() => setShowQuoted((v) => !v)}>{showQuoted ? 'Hide quoted text' : '··· Show quoted text'}</button>
          {showQuoted && <blockquote style={{ margin: '10px 0 0', paddingLeft: 14, borderLeft: '3px solid var(--km-line2)', color: 'var(--km-muted)' }}><div dangerouslySetInnerHTML={{ __html: renderMarkdown(dequote(quoted)) }} /></blockquote>}
        </div>
      )}
    </div>
  );
}

// Split a plain-text body into the author's text and the quoted history. The
// trailer begins at the first `>`-quoted line (including a preceding
// "On … wrote:" attribution) or a standalone "On … wrote:" line.
function splitQuoted(text) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let q = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*>/.test(lines[i])) { q = i; break; }
    if (i > 0 && /\bwrote:\s*$/.test(lines[i].trim()) && /^on\b/i.test(lines[i].trim())) { q = i; break; }
  }
  if (q === -1) return { main: text.trim(), quoted: '' };
  if (q > 0 && /\bwrote:\s*$/.test(lines[q - 1].trim())) q -= 1; // pull in the attribution line
  return { main: lines.slice(0, q).join('\n').trim(), quoted: lines.slice(q).join('\n').trim() };
}

// Strip one level of `> ` quote markers for clean display inside the blockquote.
function dequote(q) { return q.split('\n').map((l) => l.replace(/^\s?>\s?/, '')).join('\n'); }

function extractText(msg) { if (!msg?.bodyValues) return ''; const part = msg.textBody?.[0] || msg.htmlBody?.[0]; return msg.bodyValues[part?.partId]?.value || ''; }
// Parse a PQC body into the ARRAY of envelopes it carries (one per recipient +
// sender). Returns null if the body is not a KryptaMail encrypted message.
function tryParsePqc(raw) {
  try {
    const s = raw.replace(/^\[KryptaMail post-quantum encrypted message\]\s*/, '');
    const o = JSON.parse(s);
    const arr = Array.isArray(o) ? o : [o];
    return arr.length && arr[0] && arr[0].suite && arr[0].kem && arr[0].body ? arr : null;
  } catch { return null; }
}
// True when a plain-text body is actually a KryptaMail encrypted envelope.
export function isPqcBody(s) { return typeof s === 'string' && s.startsWith('[KryptaMail post-quantum encrypted message]'); }
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtSize = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} KB`);
